import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Label } from '@shelflix/ui-web/components/ui/label';
import { useEffect, useState } from 'react';
import Loader from '../Loader';

type MediaPreviewProps = {
  file: File;
  onReplace: () => void;
  onDelete?: () => void;
  showDeleteButton?: boolean;
  loading?: boolean;
};

export const MediaPreview = ({
  file,
  onReplace,
  onDelete,
  showDeleteButton = true,
  loading = true,
}: MediaPreviewProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<{
    name: string;
    extension: string;
    size: string;
    duration?: string;
    isVideo: boolean;
  }>({
    name: '',
    extension: '',
    size: '',
    duration: '',
    isVideo: false,
  });

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    const name = file.name ?? 'arquivo';
    const extension = file.type.split('/').pop() ?? '';
    const size = (file.size / 1024 / 1024).toFixed(2) + ' MB';
    const isVideo = ['mp4', 'mov', 'webm'].includes(extension.toLowerCase());

    setMetadata((prev) => ({
      ...prev,
      name,
      extension,
      size,
      isVideo,
    }));

    if (isVideo) {
      const video = document.createElement('video');
      video.src = url;
      video.onloadedmetadata = () => {
        const duration = video.duration;
        const minutes = Math.floor(duration / 60);
        const seconds = Math.floor(duration % 60);
        setMetadata((prev) => ({
          ...prev,
          duration: `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} segundos`,
        }));
      };
    }

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  return (
    <div className="flex w-full flex-col justify-between gap-14 sm:flex-row sm:flex-wrap">
      {loading ? (
        <Loader message="Carregando mídia..." />
      ) : (
        <>
          <div className="flex max-w-[550px] grow-0 basis-1/2 flex-col gap-10 sm:flex-row sm:items-center">
            <div className="flex flex-col sm:items-center">
              <Label className="mb-2 font-medium">Vertical</Label>
              <div className="h-[264px] w-[160px] overflow-hidden rounded border-4 border-black bg-[var(--gray-primary)]">
                {metadata.isVideo ? (
                  <video src={previewUrl ?? ''} className="h-full w-full object-contain" controls />
                ) : (
                  <img src={previewUrl ?? ''} alt="vertical" className="h-full w-full object-contain" />
                )}
              </div>
            </div>
            <div className="flex grow flex-col sm:items-center">
              <Label className="mb-2 font-medium">Horizontal</Label>
              <div className="h-[160px] w-[264px] overflow-hidden rounded border-4 border-black bg-[var(--gray-primary)]">
                {metadata.isVideo ? (
                  <video src={previewUrl ?? ''} className="h-full w-full object-contain" controls />
                ) : (
                  <img src={previewUrl ?? ''} alt="horizontal" className="h-full w-full object-contain" />
                )}
              </div>
            </div>
          </div>

          <div className="flex max-w-[550px] grow flex-col items-end justify-between text-sm">
            <div className="w-full space-y-6">
              <div className="flex justify-between">
                <span className="text-muted-foreground w-1/2">Nome Arquivo</span>
                <span className="w-1/2 font-medium">{metadata.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground w-1/2">Extensão</span>
                <span className="w-1/2 font-medium">{metadata.extension.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground w-1/2">Tamanho</span>
                <span className="w-1/2 font-medium">{metadata.size}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground w-1/2">Validação</span>
                <span className="w-1/2 font-medium text-green-600">Arquivo sem erros</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground w-1/2">Duração</span>
                <span className="w-1/2 font-medium">{metadata.duration ?? '--'}</span>
              </div>
            </div>

            <div className="mt-6 flex w-full flex-row justify-between">
              <Button variant="outline" onClick={onReplace}>
                <Icon name="compare_arrows" className="text-primary" />
                <span className="[font-family:'Figtree'] text-sm">Substituir</span>
              </Button>
              {showDeleteButton && (
                <Button variant="destructive" type="button" onClick={onDelete}>
                  <Icon name="delete" className="text-white" size={22} fill={1} />
                  <span className="[font-family:'Figtree'] text-sm">Excluir</span>
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
